import React, { useEffect, useMemo } from 'react'
import { Button, Grid, LinearProgress } from '@material-ui/core'
import { usePurchasedItemsStyles } from './purchased-items-page.styles'
import { Filter } from '../../shared/filter'
import { useScreenSize } from '../../hooks/use-screen-size'
import {
  fetchCategories,
  fetchNonprofits,
  getPurchasedItems,
  setQueryParams,
  setSortingInitial,
  resetRequestParams,
} from '../../store/purchased-items/reducer'
import { useDispatch } from 'react-redux'
import { usePurchasedItemsPage } from './purchased-items-page.hook'
import { ListedItem, ListedItemProps } from '../../components/listed-item'
import { QueryParams } from '../home-page/home-component/home.constants'
import { AppPagination as Pagination } from '../../components/app-pagination'
import { SortByDateSelect } from '../../shared/sort-by-date-select'
import { ShowItemsSelect } from '../../components/show-items-select'
import { EmptySearchResultComponent } from '../../components/empty-search-result-component'
import { PlaceholderArrayComponent } from '../../components/placeholder-array-component'
import { useAppDialog } from '../../hooks/use-app-dialog'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ScreenSize } from '../../settings/device'
import { PageWrapper } from '../../components/page-wrapper'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { GridItem } from '../../shared/grid-item'

export const PurchasedItemsPage: React.FC = () => {
  const classes = usePurchasedItemsStyles()
  const screenSize = useScreenSize()
  const dispatch = useDispatch()
  const { setIsShow, setAppDialog } = useAppDialog()
  const { t } = useTranslation()

  const {
    //props
    itemsList,
    loading,
    query,
    totalPages,
    needApply,
    firstRender,
    categories,
    nonprofits,
    isFilterCollapsed,
    userStatus,

    //methods
    changeFilterCollapsedAction,
  } = usePurchasedItemsPage()

  const navigate = useNavigate()

  const purchasedBreadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('header.menu.search'), to: '/search' },
      { label: t('purchasedItemsPage.title') },
    ],
    [t]
  )

  /*Filter Change Handler*/
  const onQueryParamsChanged = (
    data: QueryParams,
    isApply?: boolean,
    isStartPagingInBegin?: boolean
  ) => {
    dispatch(setQueryParams({ ...data, isApply, isStartPagingInBegin }))
  }

  //Get Listed Items
  useEffect(() => {
    const {
      pageSize,
      searchText,
      sortByDate,
      pageIndex,
      listedItemState,
      nonprofitCategoryId,
      nonprofitId,
    } = query

    needApply &&
      dispatch(
        getPurchasedItems({
          pageSize,
          searchText,
          sortByDate,
          pageIndex,
          listedItemState,
          ...(!nonprofitId && { nonprofitCategoryId }),
          ...(nonprofitId && { nonprofitId }),
        })
      )
  }, [query, needApply])

  /*Get categories when first loading*/
  useEffect(() => {
    dispatch(fetchCategories())
  }, [])

  /*Get nonprofits Before Loading PAge*/
  useEffect(() => {
    if (query.nonprofitCategoryId) {
      dispatch(
        fetchNonprofits({
          pageIndex: '',
          pageSize: '',
          categoryId: `${query.nonprofitCategoryId}`,
          searchName: '',
        })
      )
    }
  }, [query.nonprofitCategoryId])

  //User Activate
  useEffect(() => {
    !userStatus &&
      setAppDialog({
        isShow: true,
        title: t('purchasedItemsPage.appDialog.title'),
        message: t('purchasedItemsPage.appDialog.message'),
        variant: 'warning',
        actions: (
          <Button onClick={() => navigate('/account/security')} color="primary">
            {t('purchasedItemsPage.appDialog.btnText')}
          </Button>
        ),
        closeHandler: () => navigate('/'),
      })
  }, [userStatus])

  //After Unmount
  useEffect(() => {
    return () => {
      dispatch(resetRequestParams())
      setIsShow(false)
    }
  }, [])

  //Filter Collapse Handler
  const filterCollapseHandler = (value: boolean) => {
    dispatch(changeFilterCollapsedAction(value))
  }

  //Cancel Handler
  const cancelHandler = () => {
    dispatch(setSortingInitial())
  }

  //If user not activated
  if (!userStatus) {
    return <></>
  }

  return (
    <>
      <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
      <PageWrapper
        title={t('purchasedItemsPage.title')}
        displayGoBackButton={false}
        breadcrumbItems={purchasedBreadcrumbItems}
        titleEndContent={
          screenSize === ScreenSize.Desktop ? (
            <div className={classes.queryParamsHolder}>
              <SortByDateSelect
                value={query.sortByDate}
                onChange={(value) =>
                  onQueryParamsChanged({ sortByDate: value }, true, true)
                }
                disabled={loading}
                idSuffix="purchased-desktop"
              />
              <ShowItemsSelect
                value={query.pageSize}
                onChange={(pageSize) =>
                  onQueryParamsChanged({ pageSize }, true, true)
                }
                disabled={loading}
                label={t('purchasedItemsPage.showItems')}
                idSuffix="purchased-desktop"
              />
            </div>
          ) : undefined
        }
      >
        <Grid container spacing={1}>
            {/*Mobile Mode*/}
            {screenSize === ScreenSize.Mobile && (
              <Grid item xs={12}>
                <div className={classes.dateBlockMobile}>
                  <div className={classes.mobileFilterControl}>
                    <Filter
                      cancelHandler={cancelHandler}
                      categories={categories}
                      isFilterCollapsed={isFilterCollapsed}
                      loading={loading}
                      nonprofitCategoryId={query.nonprofitCategoryId}
                      nonprofitId={query.nonprofitId}
                      nonprofits={nonprofits}
                      onFilterChanged={onQueryParamsChanged}
                      screenSize={screenSize}
                      searchText={query.searchText}
                      itemsState={query.listedItemState}
                      setFilterCollapsed={filterCollapseHandler}
                    />
                  </div>
                  <SortByDateSelect
                    value={query.sortByDate}
                    onChange={(value) =>
                      onQueryParamsChanged({ sortByDate: value }, true, true)
                    }
                    disabled={loading}
                    idSuffix="purchased-mobile"
                    className={classes.mobileSortSelect}
                  />
                  <ShowItemsSelect
                    value={query.pageSize}
                    onChange={(pageSize) =>
                      onQueryParamsChanged({ pageSize }, true, true)
                    }
                    disabled={loading}
                    label={t('purchasedItemsPage.showItems')}
                    idSuffix="purchased-mobile"
                    className={classes.mobileShowItemsSelect}
                  />
                </div>
              </Grid>
            )}

            {/*Filter*/}
            {screenSize === ScreenSize.Desktop && (
              <Grid item xs={12} lg={3} xl={2} md={3}>
                <Filter
                  cancelHandler={cancelHandler}
                  categories={categories}
                  isFilterCollapsed={isFilterCollapsed}
                  loading={loading}
                  nonprofitCategoryId={query.nonprofitCategoryId}
                  nonprofitId={query.nonprofitId}
                  nonprofits={nonprofits}
                  onFilterChanged={onQueryParamsChanged}
                  screenSize={screenSize}
                  searchText={query.searchText}
                  itemsState={query.listedItemState}
                  setFilterCollapsed={filterCollapseHandler}
                />
              </Grid>
            )}

            {/*Content*/}
            <Grid item xs={12} xl={10} lg={9} md={9}>
              <Grid container spacing={1}>
                {firstRender && <PlaceholderArrayComponent />}
                {!itemsList.length && !firstRender && (
                  <Grid xs={12} item>
                    <EmptySearchResultComponent />
                  </Grid>
                )}
                {itemsList.map((item: ListedItemProps, key: number) => (
                  <GridItem key={key} xxxs={12} xxs={6} xs={4} md={4} lg={3} xl={2}>
                    <ListedItem
                      {...item}
                      itemUrl={`/listed-items/${item.id}`}
                      state={undefined}
                      isPurchase={true}
                      listedUtc={undefined}
                      settings={{
                        hideCity: false,
                        hideRegion: false,
                        hideCondition: true,
                      }}
                      showDate={false}
                      showSoldDate={true}
                      showOrderStatus={true}
                    />
                  </GridItem>
                ))}

                {!firstRender && totalPages > 1 && (
                  <Grid item xs={12}>
                    <Pagination
                      count={totalPages}
                      disabled={loading}
                      page={query.pageIndex + 1}
                      onChange={(_, page) =>
                        onQueryParamsChanged({ pageIndex: page - 1 }, true)
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
        </Grid>
      </PageWrapper>
    </>
  )
}
